import { useIntl } from 'react-intl'
import { Portal } from '@retailer-platform/shared-components'
import { FeatureHighlight, Link } from '@retailer-platform/shared-components/src/tds'
import { useAdminControls } from '../../../utils/contexts/admin-controls/AdminControlsContext'
import { type OnboardingStep } from './NavV3OnboardingTourSteps'
import { useAnchoredPosition } from './useAnchoredPosition'

const LEARN_MORE_URL = 'https://tools-auth.instacart.tools/login'

type Props = {
  step: OnboardingStep
  currentStep: number
  totalSteps: number
  onStepChange: (next: number) => void
  onClose: () => void
}

// Renders one popup step of the onboarding tour. The TDS FeatureHighlight
// molecule supplies the visual (label, title, description, pulse, button)
// while `useAnchoredPosition` handles DOM-anchored placement that the
// molecule itself does not provide.
//
// The "Use classic navigation instead" link is step 1 only (per Figma) and
// doubles as a soft opt-out path: it closes the tour AND flips the user back
// to v2 via the same `useTdsNav` admin control the version switcher uses.
export const NavV3OnboardingTourStep = ({
  step,
  currentStep,
  totalSteps,
  onStepChange,
  onClose,
}: Props) => {
  const intl = useIntl()
  const { setAdminControl } = useAdminControls()
  const { style, ready } = useAnchoredPosition({
    anchorSelector: step.selector,
    placement: step.placement,
    point: step.pulsePoint,
    gap: 16,
  })

  if (!ready) return null

  const isLastStep = currentStep === totalSteps - 1
  const buttonLabel = intl.formatMessage({
    id: isLastStep ? 'navV3.onboarding.tour.done' : 'navV3.onboarding.tour.next',
  })

  const switchToClassic = () => {
    onClose()
    setAdminControl('useTdsNav', false)
  }

  return (
    <Portal>
      <div css={{ ...style, zIndex: 10000 }}>
        <FeatureHighlight
          title={intl.formatMessage({ id: step.titleKey })}
          description={intl.formatMessage({ id: step.descriptionKey })}
          currentStep={currentStep}
          totalSteps={totalSteps}
          onStepChange={onStepChange}
          onClose={onClose}
          pulsePoint={step.pulsePoint}
          pulsePlacement={step.pulsePlacement}
          buttonLabel={buttonLabel}
          imageProps={{
            src: step.imgSrc,
            alt: intl.formatMessage({ id: step.imgAltKey }),
          }}
          linkProps={
            currentStep === 0
              ? {
                  href: LEARN_MORE_URL,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  children: intl.formatMessage({ id: 'navV3.onboarding.tour.learnMore' }),
                }
              : undefined
          }
        />
        {currentStep === 0 && (
          // Sits on the left side of the Next button row (the back-arrow slot,
          // which is empty on step 1). Vertically centered to align with the
          // Next button: 24 (content padding-bottom) + (40 button - 20 link) / 2.
          <div css={{ position: 'absolute', left: 24, bottom: 34 }}>
            <Link onPress={switchToClassic}>
              {intl.formatMessage({ id: 'navV3.onboarding.tour.useClassicNav' })}
            </Link>
          </div>
        )}
      </div>
    </Portal>
  )
}
